const bluetoothBaseUuid = (shortId) =>
  `0000${shortId.toString(16).padStart(4, "0")}-0000-1000-8000-00805f9b34fb`;

// ESC/POS Bluetooth printers are sold with several interchangeable BLE
// modules. These are the common UART-style services used by those modules.
const SHORT_SERVICE_IDS = [
  0xffe0,
  0xffe1,
  0xffe5,
  0x18f0,
  0x18f1,
  0xff00,
  0xff01,
  0xff02,
  0xfff0,
  0xae30,
  0xae3a,
  0xaf30,
];

const CUSTOM_SERVICE_IDS = [
  // Nordic UART Service.
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e",
  // Microchip Transparent UART Service.
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",
];

export const BLE_PRINTER_OPTIONAL_SERVICES = [
  ...SHORT_SERVICE_IDS,
  ...CUSTOM_SERVICE_IDS,
];

export const BLE_PRINTER_SERVICE_UUIDS = [
  ...SHORT_SERVICE_IDS.map(bluetoothBaseUuid),
  ...CUSTOM_SERVICE_IDS,
];

export const BLE_PRINTER_CHARACTERISTIC_UUIDS = [
  0xffe1,
  0xffe2,
  0xff01,
  0xff02,
  0xfff1,
  0xfff2,
  0xae01,
  0xae02,
  0xae3b,
  0xaf01,
  0x2af1,
].map(bluetoothBaseUuid).concat([
  "6e400002-b5a3-f393-e0a9-e50e24dcca9e",
  "49535343-8841-43f4-a8d4-ecbe34729bb3",
]);

const delay = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

const isWritable = (characteristic) => {
  const properties = characteristic?.properties || {};
  return Boolean(properties.write || properties.writeWithoutResponse);
};

const findWritableInService = async (service) => {
  try {
    const characteristics = await service.getCharacteristics();
    const writable = characteristics.find(isWritable);
    if (writable) return writable;
  } catch {
    // Some inexpensive BLE modules fail bulk discovery but still allow a
    // characteristic to be requested directly by UUID below.
  }

  for (const characteristicId of BLE_PRINTER_CHARACTERISTIC_UUIDS) {
    try {
      const characteristic = await service.getCharacteristic(characteristicId);
      if (isWritable(characteristic)) return characteristic;
    } catch {
      // Try the next common UART characteristic.
    }
  }
  return null;
};

export const discoverWritableBleCharacteristic = async (
  server,
  { attempts = 3, retryDelayMs = 280, onAttempt } = {}
) => {
  if (!server) return null;
  let lastDiscoveryError = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    onAttempt?.(attempt + 1, attempts);
    let services = [];
    try {
      services = await server.getPrimaryServices();
    } catch (error) {
      lastDiscoveryError = error;
    }

    for (const service of services) {
      const characteristic = await findWritableInService(service);
      if (characteristic) return characteristic;
    }

    // `getPrimaryServices()` can return "No Services found" on printer
    // modules that expose only an explicitly requested vendor service.
    for (const serviceId of BLE_PRINTER_SERVICE_UUIDS) {
      try {
        const service = await server.getPrimaryService(serviceId);
        const characteristic = await findWritableInService(service);
        if (characteristic) return characteristic;
      } catch (error) {
        lastDiscoveryError = error;
      }
    }

    if (attempt < attempts - 1) await delay(retryDelayMs);
  }

  if (lastDiscoveryError) {
    console.warn("[BLE] Writable printer service discovery failed", lastDiscoveryError);
  }
  return null;
};

